import {
  Box,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MessagesOutlineIcon from 'assets/icons/messages-outline.svg';
import { DrawerProvider } from 'components/@home/@documents/DrawerProvider';
import DocumentFormatEditDrawer, {
  EditDrawerContext as DocumentFormatEditDrawerContext,
} from 'components/@home/@documents/SentDocuments/EditDrawer';
import DocumentFormatSendDrawer from 'components/@home/@documents/SentDocuments/SendDrawer';
import { SendDrawerContext as DocumentFormatSendDrawerContext } from 'components/@home/@documents/SentDocuments/SendDrawerContext';
import SendDrawer, {
  SendDrawerContext,
} from 'components/@home/@documents/SharedDocuments/SendDrawer';
import ShareDrawer, {
  ShareDrawerContext,
} from 'components/@home/@documents/SharedDocuments/ShareDrawer';
import FolderArrowRightIcon from 'mdi-react/FolderArrowRightIcon';
import FolderHomeOutlineIcon from 'mdi-react/FolderHomeOutlineIcon';
import FolderSharedOutlineIcon from 'mdi-react/FolderSharedOutlineIcon';
import FileSendOutlineIcon from 'mdi-react/FileSendOutlineIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import React from 'react';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import { Link, Outlet, useLocation } from 'react-router';
import headerActions from 'store/app/ui/header/action';
import isCompanyAdminSelector from 'store/selectors/isCompanyAdmin';
import isTeamAdminSelector from 'store/selectors/isTeamAdmin';
import useActions from 'utils/useActions';

const icons = [
  { name: 'home', path: 'home', Icon: () => <FolderHomeOutlineIcon /> },
  { name: 'shared', path: 'shared', Icon: () => <FolderSharedOutlineIcon /> },
  {
    name: 'chats',
    path: 'chats',
    Icon: () => (
      <SvgIcon>
        <MessagesOutlineIcon />
      </SvgIcon>
    ),
  },
  { name: 'separator-1', role: 'teamAdmin' },
  { name: 'tracking', path: 'tracking', Icon: () => <FileSendOutlineIcon />, role: 'teamAdmin' },
  {
    name: 'templates',
    path: 'templates',
    Icon: () => <DescriptionOutlinedIcon />,
    role: 'teamAdmin',
  },
  {
    name: 'sent',
    path: 'sent',
    Icon: () => <FolderArrowRightIcon />,
    role: 'teamAdmin',
    hidden: true,
  },
  { name: 'separator-2' },
  // ['recent', () => <ClockOutlineIcon />],
  // ['starred', () => <StarOutlineIcon />],
  // ['separator-2'],
  { name: 'deleted', path: 'deleted', Icon: () => <TrashCanOutlineIcon /> },
].map(item => ({ role: 'member', ...item }));

const DocumentsScene = () => {
  const location = useLocation();
  const isTeamAdmin = useSelector(isTeamAdminSelector);
  const isCompanyAdmin = useSelector(isCompanyAdminSelector);
  const header = useActions(headerActions);
  const channelsPannelOpen = useSelector(state => state.header.channelsPannelOpen);
  return (
    <Grid container sx={{ flex: 1 }}>
      <Grid
        sx={[
          {
            borderRight: 1,
            flex: 0,
            borderColor: 'divider',
            position: { xs: 'absolute', md: 'static' },
            overflow: 'hidden',
            width: {
              md: 'auto',
            },
            backgroundColor: 'background.paper',
            zIndex: { xs: 2, md: 0 },
            minWidth: { md: 300 },
          },
          channelsPannelOpen
            ? {
                width: {
                  xs: 300,
                },
              }
            : {
                width: {
                  xs: 0,
                },
              },
        ]}
      >
        <Box sx={{ padding: 2 }}>
          <Typography variant="h5">{I18n.t('DocumentsScene.Title')}</Typography>
        </Box>
        <List>
          {icons
            .filter(
              i =>
                !i.hidden &&
                (i.role === 'member' ||
                  (i.role === 'teamAdmin' && isTeamAdmin) ||
                  (i.role === 'companyAdmin' && isCompanyAdmin)),
            )
            .map(({ name, path, Icon }) =>
              name.startsWith('separator-') ? (
                <Box
                  key={name}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    marginTop: 1,
                    marginBottom: 1,
                  }}
                />
              ) : (
                <ListItemButton
                  key={name}
                  to={`/home/documents/${path}/`}
                  component={Link}
                  selected={!!location.pathname.match(new RegExp(`/documents/${path}(/|$)`))}
                  sx={{ paddingLeft: 4, paddingRight: 4 }}
                  onClick={() => channelsPannelOpen && header.toggleChannelsPanel()}
                >
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      whiteSpace: 'nowrap',
                      borderBottom: 0,
                      minHeight: 0,
                      paddingTop: 0,
                      paddingBottom: 0,
                    }}
                    primary={I18n.t(`DocumentsScene.Folders.${name}`)}
                  />
                </ListItemButton>
              ),
            )}
        </List>
      </Grid>
      <DrawerProvider context={DocumentFormatSendDrawerContext}>
        <DrawerProvider context={DocumentFormatEditDrawerContext}>
          <DrawerProvider context={SendDrawerContext}>
            <DrawerProvider context={ShareDrawerContext}>
              <Outlet />
              <ShareDrawer />
              <SendDrawer />
              <DocumentFormatSendDrawer />
              <DocumentFormatEditDrawer />
            </DrawerProvider>
          </DrawerProvider>
        </DrawerProvider>
      </DrawerProvider>
    </Grid>
  );
};

export default DocumentsScene;
